import {LoginResponesModel} from 'models';
import {ItemDataFieldModel} from 'models/board';
import {SettingsKeyValueResponseModel, TenantSettingsKeysEnum} from 'models/settings';
import {TenantUserResponseModel} from 'models/tenant.model';
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {clearSession, sessionSelector, setSession, updateTenatSettingsRedux} from 'store/auth/sessionSlice';
import {setSelectedSprint} from 'store/sprints';

export const useSessionRedux = () => {
    const sessionRedux = useSelector(sessionSelector);

    const dispatch = useDispatch();

    const updateDefaultTenant = useCallback(
        (value: TenantUserResponseModel) => {
            const copySession = {...sessionRedux};
            copySession.tenantId = value.updatedTanentId;
            copySession.jwtToken = value.jwtToken;
            copySession.tenantSlug = value.updatedTanentSlug;
            copySession.workspaceSettings = value.workspaceSettings;
            copySession.tenantSettings = value.tenantSettings;
            copySession.boardSettings = value.boardSettings;

            dispatch(setSession(copySession));
        },
        [sessionRedux],
    );

    const setSessionInRedux = useCallback(
        (params: LoginResponesModel) => {
            dispatch(setSession(params));
        },
        [dispatch],
    );

    const clearSessionInRedux = useCallback(() => {
        dispatch(clearSession());
    }, [dispatch]);
    const refrestTokenHandler = useCallback(
        (response: {refreshToken: string; token: string}) => {
            const copySession = {...sessionRedux};
            copySession.jwtToken = response.token;
            copySession.jwtToken = response.refreshToken;
            dispatch(setSession(copySession));
        },
        [sessionRedux],
    );

    const updateTenantSettingValue = useCallback(
        (response: ItemDataFieldModel[]) => {
            if (!response) return;

            // Create lookup for faster find
            const responseMap = new Map(response.map(x => [x.uniqueId, x]));

            const updatedSettings = sessionRedux.tenantSettings.map(item => {
                const matched = responseMap.get(item.uniqueId);

                // Special case: TenantEnableSprints disabled
                if (
                    matched?.uniqueId === TenantSettingsKeysEnum.TenantEnableSprints &&
                    matched?.value.booleanValue === false
                ) {
                    dispatch(setSelectedSprint(null));
                }

                return matched ? {...item, value: matched.value} : item; // keep original if not found
            });

            // Add new settings that are in response but not in existing tenantSettings
            const existingIds = new Set(sessionRedux.tenantSettings.map(x => x.uniqueId));
            const newSettings: SettingsKeyValueResponseModel[] = response
                .filter(r => !existingIds.has(r.uniqueId))
                .map(
                    r =>
                        ({
                            uniqueId: r.uniqueId,
                            value: r.value,
                            dataType: r.dataType,
                        }) as SettingsKeyValueResponseModel,
                );

            dispatch(updateTenatSettingsRedux([...updatedSettings, ...newSettings]));
        },
        [sessionRedux.tenantSettings],
    );

    return {
        refrestTokenHandler,
        updateTenantSettingValue,
        setSessionInRedux,
        clearSessionInRedux,
        updateDefaultTenant,
    };
};
